/* Career Pathways & Internships: /career-pathways
   ----------------------------------------------------------------------
   One of the four main sections (owner, 2026-10-06), in the shared
   catalogue structure.

   Career Pathways is Poulton Consultancy's programme (7 October 2026).
   The page follows the Poulton x Kidsology Partnership & Referral
   Agreement's display rules (see POULTON and PATHWAYS_PAGE in
   src/data/offer.js): Poulton's tiles are shown exactly as supplied
   (public/partners/poulton/career-pathways-live-tiles.html, in a frame
   so no site style touches them), every booking link is Poulton's
   tracked link, the wording is "with Poulton Consultancy" (Kidsology
   helps run it, owner 2026-10-07), and Kidsology collects no Career Pathways registrations. Arabic and
   Russian visitors also get a faithful summary, since the tiles are in
   English.

   Internships (ages 15 to 18, arranged by Kidsology and Poulton
   together) keep Kidsology's enquiry form, writing to
   `partnershipLeads` with partnerType "career-pathways". */

import { SIZES } from "../lib/img";
import React, { useEffect, useRef, useState } from "react";
import SEO from "../components/SEO";
import SiteHeader from "../components/SiteHeader";
import LocaleLink from "../components/LocaleLink";
import { useLocale, useL } from "../i18n/LocaleContext";
import { PATHWAYS_PAGE, POULTON, SECTION, CONTACTS } from "../data/offer";
import { SCHOOLS } from "../data/schools";
import { breadcrumbSchema, careerPathwaysServiceSchema, faqPageSchema } from "../data/schemas";
import { ARROW, Pic, CheckList, FloatingCta, useHashScroll, Accent, SectionHead, PartnerEnquiryForm, ContactList, FaqList } from "../components/catalog/CatalogParts";
import { track } from "../lib/analytics";

const PATH = "/career-pathways";
const external = { target: "_blank", rel: "noopener noreferrer" };

/* Poulton's tiles, unaltered, in a same-origin frame sized to its
   content. Client-only (the frame is not in the pre-rendered HTML), so
   the snapshot and React's first render agree. */
function PoultonTiles({ title }) {
  const [mounted, setMounted] = useState(false);
  const [height, setHeight] = useState(1300);
  const frameRef = useRef(null);
  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    const frame = frameRef.current;
    if (!mounted || !frame) return undefined;
    let ro;
    const fit = () => {
      const body = frame.contentDocument && frame.contentDocument.body;
      if (body && body.offsetHeight) setHeight(Math.ceil(body.offsetHeight));
    };
    const onLoad = () => {
      const doc = frame.contentDocument;
      if (!doc || doc.URL === "about:blank") return;
      fit();
      if (ro) ro.disconnect();
      if ("ResizeObserver" in window) {
        ro = new ResizeObserver(fit);
        ro.observe(doc.body);
      }
    };
    frame.addEventListener("load", onLoad);
    onLoad();
    return () => { frame.removeEventListener("load", onLoad); if (ro) ro.disconnect(); };
  }, [mounted]);
  return (
    <div className="cat-partner-frame-wrap" style={{ minHeight: mounted ? undefined : 600 }}>
      {mounted && (
        <iframe
          ref={frameRef}
          src={POULTON.tiles}
          title={title}
          className="cat-partner-frame"
          style={{ height }}
          data-prerender="omit"
        />
      )}
    </div>
  );
}

export default function CareerPathways() {
  const { locale } = useLocale();
  const c = useL(PATHWAYS_PAGE);
  const contacts = useL(CONTACTS);
  const name = useL(SECTION.pathways);
  const translated = locale !== "en";
  useHashScroll();

  return (
    <div className="cat-page">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        path={PATH}
        image={`${PATHWAYS_PAGE.hero.photos[0].src}.jpg`}
        schema={[
          careerPathwaysServiceSchema(locale),
          breadcrumbSchema([
            { name: SCHOOLS.home, path: "/" },
            { name: SECTION.pathways, path: PATH },
          ], locale),
          faqPageSchema(PATHWAYS_PAGE.faq.items, locale),
        ]}
      />
      <SiteHeader ctaHref="#programme" />
      <main id="main">
        {/* ── Hero ── */}
        <header className="cat-hero">
          <div className="cat-hero-inner">
            <div className="cat-hero-copy">
              <p className="cat-eyebrow">{name}</p>
              <h1 className="cat-title"><Accent title={c.hero.title} /></h1>
              <p className="cat-lead">{c.hero.lead}</p>
              <ul className="cat-stats cat-stats--words">
                {c.hero.stats.map((s, i) => (
                  <li key={i}>
                    <span className="cat-stat-v">{s.v}</span>
                    <span className="cat-stat-l">{s.l}</span>
                  </li>
                ))}
              </ul>
              <div className="cat-actions cat-hero-actions">
                <a href="#programme" className="btn btn-gold-lg" onClick={() => track("pathways_hero_cta_click", { target: "programme" })}>{c.hero.ctaProgramme}</a>
                <a href={POULTON.bookParents} {...external} className="btn btn-outline-white" onClick={() => track("pathways_poulton_click", { link: "parents", placement: "hero" })}>{c.hero.ctaBook}</a>
              </div>
            </div>
            <div className="cat-mosaic">
              {c.hero.photos.map((p, i) => <Pic key={p.src} src={p.src} alt={p.alt} eager={i < 2} sizes={SIZES.strip} />)}
            </div>
          </div>
        </header>

        {/* ── Career Pathways, with Poulton Consultancy ── */}
        <section className="cat-section cat-section--white" id="programme" aria-labelledby="cp-title">
          <div className="cat-wrap">
            <SectionHead id="cp-title" eyebrow={c.programme.eyebrow} title={c.programme.title} lead={c.programme.lead} />

            {translated && (
              <div className="cat-two">
                {[["schools", POULTON.bookSchools], ["families", POULTON.bookParents]].map(([key, href]) => (
                  <div key={key} className="cat-panel cat-panel--accent">
                    <h3 className="cat-panel-title">{c.programme[key].title}</h3>
                    <CheckList items={c.programme[key].items} />
                    <p className="cat-small">
                      <a href={href} {...external} className="cat-info-link" onClick={() => track("pathways_poulton_click", { link: key === "schools" ? "schools" : "parents", placement: "summary" })}>
                        {c.programme[key].cta} {ARROW}
                      </a>
                    </p>
                  </div>
                ))}
              </div>
            )}
            {translated && <p className="cat-line">{c.programme.englishNote}</p>}

            <PoultonTiles title={c.programme.tilesTitle} />

            <p className="cat-line">
              {c.programme.contact}{" "}
              <a href={POULTON.whatsapp} {...external} onClick={() => track("pathways_poulton_click", { link: "whatsapp" })}>{c.programme.whatsapp}</a>
              {" · "}
              <a href={`tel:${POULTON.phone.replace(/\s+/g, "")}`}><bdi dir="ltr">{POULTON.phone}</bdi></a>
              {" · "}
              <a href={`mailto:${POULTON.email}?subject=Career%20Pathways%20enquiry%20via%20Kidsology`}><bdi>{POULTON.email}</bdi></a>
            </p>
          </div>
        </section>

        {/* ── Internships, and the Showcase ── */}
        <section className="cat-section" id="internships" aria-labelledby="in-title">
          <div className="cat-wrap cat-split">
            <div className="cat-panel cat-panel--accent">
              <p className="cat-eyebrow">{c.internships.eyebrow}</p>
              <h2 id="in-title" className="cat-panel-title">{c.internships.title.main} {c.internships.title.accent}</h2>
              <p className="cat-text">{c.internships.text}</p>
              <p className="cat-small">
                <a href="#enquire" className="cat-info-link" onClick={() => track("pathways_internships_click")}>{c.internships.cta} {ARROW}</a>
              </p>
            </div>
            <div className="cat-panel cat-panel--green">
              <h2 className="cat-panel-title">{c.showcase.title}</h2>
              <p className="cat-text">{c.showcase.text}</p>
              <p className="cat-small">
                <LocaleLink to="/schools#showcase" className="cat-info-link">{c.showcase.linkShowcase} {ARROW}</LocaleLink>
              </p>
              <p className="cat-small">
                <LocaleLink to="/programs" className="cat-info-link">{c.showcase.linkPrograms} {ARROW}</LocaleLink>
              </p>
            </div>
          </div>
        </section>

        {/* ── Questions parents and schools ask ── */}
        <section className="cat-section cat-section--white" id="faq" aria-labelledby="faq-title">
          <div className="cat-wrap cat-wrap--narrow">
            <SectionHead id="faq-title" eyebrow={c.faq.eyebrow} title={c.faq.title} />
            <FaqList items={c.faq.items} />
          </div>
        </section>

        {/* ── Internship enquiry ── */}
        <section className="cat-section cat-section--navy" id="enquire" aria-labelledby="enquire-title">
          <div className="cat-enquiry-inner">
            <div>
              <p className="cat-eyebrow">{c.enquiry.eyebrow}</p>
              <h2 id="enquire-title" className="cat-h2"><Accent title={c.enquiry.title} /></h2>
              <p className="cat-sub">{c.enquiry.lead}</p>
              <ContactList contacts={contacts} />
            </div>
            <div className="cat-form">
              <PartnerEnquiryForm copy={c.enquiry.form} options={c.enquiry.options} partnerType="career-pathways" event="pathways_enquiry_submit" idPrefix="cp" />
            </div>
          </div>
        </section>
      </main>
      <FloatingCta
        href="#programme"
        label={c.floatCta}
        heroSelector=".cat-hero-actions"
        targetId="programme"
        event="pathways_float_cta_click"
      />
    </div>
  );
}
